// oxlint-disable no-unused-vars
import { UnboundError } from "./consts";
import type { FastEvent } from "./event";
import { FastListenerExecutor } from "./executors/types";
import {
    ScopeEvents,
    GetClosestEvents,
    KeyOf,
    MutableMessage,
    UnTransformedEvents,
    ReplaceWildcard,
    GetPayload,
    Fallback,
    GetClosestEventPayload,
    FastEventListeners,
    Expand,
    TypedFastEventAnyListener,
} from "./types";
import {
    FastEventEmitMessage,
    TypedFastEventMessage,
    FastEventMeta,
    FastEventMessage,
} from "./types/FastEventMessages";
import { FastEventSubscriber } from "./types/FastEventSubscribers";
import { TypedFastEventListener, FastEventCommonListener } from "./types/FastEventListeners";
import {
    FastEventListenerArgs,
    FastEventListenOptions,
    FastEventListenerFlags,
} from "./types/FastEvents";
import { IsTransformedEvent } from "./types/transformed/IsTransformedEvent";
import { ExtendWildcardEvents } from "./types/wildcards/ExtendWildcardEvents";
import { PayloadValues } from "./types/transformed/PayloadValues";
import { ValueOf } from "./types/utils/ValueOf";
import { Dict } from "./types/utils/Dict";
import { DeepPartial } from "./types/utils/DeepPartial";
import { IsAllTransformed } from "./types/transformed/IsAllTransformed";
import { parseEmitArgs } from "./utils/parseEmitArgs";
import { parseScopeArgs } from "./utils/parseScopeArgs";
import { renameFn } from "./utils/renameFn";
import { PickPayload } from "./types/transformed/PickPayload";
import { isFunction } from "./utils/isFunction";
import { FastEventIterator } from "./iterator";
import { GetClosestMessage } from "./types/closest/GetClosestMessage";
import { InMatchedEvent } from "./types/wildcards/InMatchedEvent";

export type FastEventScopeOptions<Meta = Record<string, any>, Context = never> = {
    meta: FastEventScopeMeta & FastEventMeta & Meta;
    context: Context;
    executor?: FastListenerExecutor;
    /**
     * 对接收到的消息进行转换，用于将消息转换成其他格式
     *
     * new FastEvent({
     *    transform:(message)=>{
     *        message.payload
     *    }
     * })
     */
    transform?: (message: FastEventMessage) => any;
};

export interface FastEventScopeMeta {
    scope: string;
}

export interface IFastEventScope<
    Events extends Record<string, any> = Record<string, any>,
    Meta extends Record<string, any> = Record<string, any>,
    Context = never,
> {
    __FastEventScope__: boolean;
    readonly __events__: Events;
    readonly __meta__: Meta;
    readonly __context__: Context;
}

export class FastEventScope<
    Events extends Record<string, any> = Record<string, any>,
    Meta extends Record<string, any> = Record<string, any>,
    Context = never,
    Types extends keyof Events = keyof Events,
    FinalMeta extends Record<string, any> = FastEventMeta & FastEventScopeMeta & Meta,
> {
    __FastEventScope__: boolean = true;
    // 添加类型标记
    readonly __events__!: Events;
    readonly __meta__!: Meta & FastEventScopeMeta;
    readonly __context__!: Context;
    private _options: DeepPartial<FastEventScopeOptions<FinalMeta, Context>> = {};

    declare types: {
        events: ExtendWildcardEvents<Events>;
        eventNames: KeyOf<ExtendWildcardEvents<Events>>;
        messages: MutableMessage<Events, FinalMeta>;
        rawEvents: Events;
        meta: FinalMeta;
        listeners: FastEventListeners<Events, Expand<FastEventMeta & Meta & Record<string, any>>>;
        anyListener: TypedFastEventAnyListener<
            Events,
            Expand<FastEventMeta & Meta & Record<string, any>>
        >;
        listener: FastEventCommonListener<
            FastEventMessage,
            Expand<FastEventMeta & Meta & Record<string, any>>
        >;
    };
    prefix: string = "";
    emitter!: FastEvent<any>;
    constructor(options?: DeepPartial<FastEventScopeOptions<FinalMeta, Context>>) {
        this._options = Object.assign(
            {},
            this._initOptions(options),
        ) as unknown as FastEventScopeOptions<FinalMeta, Context>;
    }
    get context(): Fallback<Context, typeof this> {
        return (this.options.context || this) as Fallback<Context, typeof this>;
    }
    get options() {
        return this._options as FastEventScopeOptions<FinalMeta, Context>;
    }
    /**
     * 获取监听器
     * @returns 监听器
     */
    get listeners() {
        return this.emitter.getListeners(this.prefix);
    }
    bind(
        emitter: FastEvent<any>,
        prefix: string,
        options?: DeepPartial<FastEventScopeOptions<FinalMeta, Context>>,
    ) {
        this.emitter = emitter;
        this._options = Object.assign(
            this._options,
            {
                scope: prefix,
            },
            options,
        ) as unknown as Required<FastEventScopeOptions<FinalMeta, Context>>;
        if (prefix.length > 0 && !prefix.endsWith(emitter.options.delimiter!)) {
            this.prefix = prefix + emitter.options.delimiter;
        }
    }

    /**
     * 初始化选项
     *
     * 本方法主要供子类重载
     *
     * @param initial - 可选的初始字典对象
     * @returns 返回传入的初始字典对象
     */
    _initOptions(initial: Dict | undefined) {
        return initial;
    }
    /**
     * 获取作用域监听器
     * 当启用作用域时,对原始监听器进行包装,添加作用域前缀处理逻辑
     * @param listener 原始事件监听器
     * @returns 包装后的作用域监听器
     * @private
     */
    private _getScopeListener(listener: TypedFastEventListener): TypedFastEventListener {
        const scopePrefix = this.prefix;
        if (scopePrefix.length === 0) return listener;
        // 如果没有指定监听器，则使用onMessage作为监听器
        if (!listener) {
            throw new Error();
        }
        // oxlint-disable-next-line typescript/no-this-alias
        const scopeThis = this;
        const scopeListener = renameFn(function (
            message: TypedFastEventMessage,
            args: FastEventListenerArgs,
        ) {
            const type = args.rawEventType || message.type;
            if (type.startsWith(scopePrefix)) {
                // 1. 判断是否经过全局Transform转换
                // 消息是否经过转换，如果经过转换，由于结果不确定，所以不进行前缀处理，直接将消息传递给监听器
                const isTransformed = ((args.flags || 0) & FastEventListenerFlags.Transformed) > 0;
                let msg = isTransformed
                    ? message
                    : Object.assign({}, message, {
                          type: type.substring(scopePrefix.length),
                      });

                return listener.call(scopeThis.context, msg, args);
            }
        }, listener.name) as TypedFastEventListener;
        return scopeListener;
    }
    private _getScopeType(type: string) {
        return type === undefined ? undefined : this.prefix + type;
    }
    private _fixScopeType(type: string) {
        return type.startsWith(this.prefix) ? type.substring(this.prefix.length) : type;
    }
    public on<T extends string = KeyOf<Events> | "**">(
        type: T,
        options?: FastEventListenOptions<Events, FinalMeta>,
    ): T extends IsTransformedEvent<Events, T>
        ? FastEventIterator<
              T extends "**"
                  ? IsAllTransformed<Events> extends true
                      ? PayloadValues<Events>
                      : any
                  : PickPayload<ValueOf<GetClosestEvents<Events, T>>>
          >
        : FastEventIterator<
              T extends "**" ? MutableMessage<Events> : GetClosestMessage<Events, T, FinalMeta>
          >;

    // 指定监听器
    public on<T extends string = KeyOf<Events> | "**">(
        type: T,
        listener: FastEventCommonListener<
            T extends IsTransformedEvent<Events, T>
                ? PickPayload<ValueOf<GetClosestEvents<Events, T>>>
                : T extends "**"
                  ? MutableMessage<Events>
                  : GetClosestMessage<Events, T, FinalMeta>,
            FinalMeta,
            Fallback<Context, typeof this>
        >,
        options?: FastEventListenOptions,
    ): FastEventSubscriber;
    public on(): FastEventSubscriber | FastEventIterator<any> {
        if (!this.emitter) throw new UnboundError();
        const args = [...arguments] as [any, any, any];
        args[0] = this._getScopeType(args[0]);
        args[1] = this._getScopeListener(args[1]);
        return this.emitter.on(...args);
    }
    /**
     * 只订阅一次
     * @param type
     * @param options
     */
    public once<T extends string = KeyOf<Events> | "**">(
        type: T,
        listener: FastEventCommonListener<
            T extends IsTransformedEvent<Events, T>
                ? PickPayload<ValueOf<GetClosestEvents<Events, T>>>
                : T extends "**"
                  ? MutableMessage<Events>
                  : GetClosestMessage<Events, T, FinalMeta>,
            Meta,
            Fallback<Context, typeof this>
        >,
        options?: FastEventListenOptions,
    ): FastEventSubscriber;
    public once(): FastEventSubscriber {
        return this.on(arguments[0], arguments[1], Object.assign({}, arguments[2], { count: 1 }));
    }
    /**
     * 订阅全部
     * @param options
     */
    public onAny(
        options?: FastEventListenOptions<Events, FinalMeta>,
    ): FastEventIterator<MutableMessage<Events, FinalMeta>>;

    public onAny(
        listener: FastEventCommonListener<
            MutableMessage<Events, FinalMeta>,
            Meta,
            Fallback<Context, typeof this>
        >,
        options?: FastEventListenOptions<Events, FinalMeta>,
    ): FastEventSubscriber;
    onAny() {
        return this.on("**" as any, ...(arguments as any)) as any;
    }

    off(listener: TypedFastEventListener<any, any, any>): void;
    off(type: string, listener: TypedFastEventListener<any, any, any>): void;
    off(type: Types, listener: TypedFastEventListener<any, any, any>): void;
    off(type: string): void;
    off(type: Types): void;
    off() {
        const args = arguments as unknown as [any, any];
        if (typeof args[0] === "string") {
            args[0] = this._getScopeType(args[0]);
        }
        this.emitter.off(...args);
    }
    offAll() {
        this.emitter.offAll(this.prefix.substring(0, this.prefix.length - 1));
    }
    clear() {
        this.emitter.clear(this.prefix.substring(0, this.prefix.length - 1));
    }
    /**
     * 触发事件
     * @param type
     * @param directive
     */
    public emit<R = any, T extends Types = Types>(
        type: T,
        payload?: UnTransformedEvents<Events>[T],
        retain?: boolean,
    ): R[];
    public emit<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<Events>, T>
            : any,
        retain?: boolean,
    ): R[];
    public emit<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<Events>, T>
            : any,
        options?: FastEventListenerArgs<Partial<FinalMeta>>,
    ): R[];
    public emit<R = any, T extends KeyOf<Events> = KeyOf<Events>>(
        message: FastEventEmitMessage<T, UnTransformedEvents<Events>[T], Partial<FinalMeta>>,
        retain?: boolean,
    ): R[];
    public emit<R = any>(message: MutableMessage<Events, FinalMeta>, retain?: boolean): R[];
    public emit<R = any>(
        message: {
            type: keyof Events;
        },
        retain?: boolean,
    ): R[];
    public emit<R = any, T extends string = string>(
        type: T,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<Events>, T>
            : any,
        retain?: boolean,
    ): R[];
    public emit(): any {
        let [message, args] = parseEmitArgs(
            arguments,
            this.emitter.options.meta,
            this.options.meta,
            this.options.executor,
        );

        this._transformMessage(message as any, args);
        message.type = this._getScopeType(message.type)!;
        return this.emitter.emit(message as any, args);
    }

    private _transformMessage(message: FastEventMessage, args: FastEventListenerArgs<FinalMeta>) {
        if (isFunction(this._options.transform)) {
            args.rawEventType = this._getScopeType(message.type)!;
            args.flags = (args.flags || 0) | FastEventListenerFlags.Transformed;
            message.payload = this._options.transform.call(this, message);
        }
    }

    public async emitAsync<R = any, T extends Types = Types>(
        type: T,
        payload?: UnTransformedEvents<Events>[T],
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<Events>, T>
            : any,
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any, T extends string = string>(
        type: ReplaceWildcard<T> | Types,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<Events>, T>
            : any,
        options?: FastEventListenerArgs<Partial<FinalMeta>>,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any, T extends KeyOf<Events> = KeyOf<Events>>(
        message: FastEventEmitMessage<T, UnTransformedEvents<Events>[T], Partial<FinalMeta>>,
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any>(
        message: MutableMessage<Events, FinalMeta>,
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any>(
        message: {
            type: keyof Events;
        },
        retain?: boolean,
    ): Promise<(R | Error)[]>;
    public async emitAsync<R = any, T extends string = string>(
        type: T,
        payload?: InMatchedEvent<Events, T> extends true
            ? GetPayload<UnTransformedEvents<Events>, T>
            : any,
        retain?: boolean,
    ): Promise<(R | Error)[]>;

    public async emitAsync<R = any>(): Promise<[R | Error][]> {
        const results = await Promise.allSettled(this.emit.apply(this, arguments as any));
        return results.map((result) => {
            if (result.status === "fulfilled") {
                return result.value;
            } else {
                return result.reason;
            }
        });
    }

    public async waitFor<T extends string = KeyOf<Events>>(
        type: T,
        timeout?: number,
    ): Promise<
        T extends IsTransformedEvent<Events, T>
            ? PickPayload<ValueOf<GetClosestEvents<Events, T>>>
            : FastEventMessage<T, GetClosestEventPayload<Events, T>, FinalMeta>
    >;
    public async waitFor<T extends string = string>(
        type: T,
        timeout?: number,
    ): Promise<TypedFastEventMessage<Events, FinalMeta>>;

    public async waitFor(): Promise<any> {
        const type = arguments[0] as string;
        const timeout = arguments[1] as number;
        const message = await this.emitter.waitFor(this._getScopeType(type)!, timeout);
        const scopeMessage = Object.assign({}, message, {
            type: this._fixScopeType(message.type),
        });
        return scopeMessage;
    }
    /**
     * 创建一个新的作用域实例
     * @param prefix - 作用域前缀
     * @returns 新的FastEventScope实例
     *
     * @description
     * 基于当前作用域创建一个新的子作用域。新作用域会继承当前作用域的所有特性，
     * 并在事件类型前添加额外的前缀。这允许创建层级化的事件命名空间。
     *
     * 作用域的特性：
     * - 自动为所有事件类型添加前缀
     * - 在触发事件时自动添加前缀
     * - 在接收事件时自动移除前缀
     * - 支持多层级的作用域嵌套
     *
     * @example
     * ```ts
     * const emitter = new FastEvent();
     * const userScope = emitter.scope('user');
     * const profileScope = userScope.scope('profile');
     *
     * // 在profileScope中监听'update'事件
     * // 实际监听的是'user/profile/update'
     * profileScope.on('update', (data) => {
     *   console.log('Profile updated:', data);
     * });
     *
     * // 在profileScope中触发'update'事件
     * // 实际触发的是'user/profile/update'
     * profileScope.emit('update', { name: 'John' });
     * ```
     */

    scope<
        E extends Record<string, any> = Record<string, any>,
        P extends string = string,
        M extends Record<string, any> = Record<string, any>,
        C = Context,
    >(
        prefix: P,
        options?: DeepPartial<FastEventScopeOptions<Partial<FinalMeta> & M, C>>,
    ): FastEventScope<ScopeEvents<Events & E, P>, FinalMeta & M, C>;
    scope<
        E extends Record<string, any> = Record<string, any>,
        P extends string = string,
        M extends Record<string, any> = Record<string, any>,
        C = Context,
        ScopeInstance extends IFastEventScope<Record<string, any>, any, any> = FastEventScope<
            Record<string, any>,
            any,
            any
        >,
    >(
        prefix: P,
        scopeObj: ScopeInstance,
        options?: DeepPartial<FastEventScopeOptions<Partial<FinalMeta> & M, C>>,
    ): FastEventScope<ScopeEvents<Events & E, P>, FinalMeta & M, C> & ScopeInstance; //FastEventScopeExtend<Events & E, P, ScopeInstance, Meta>;
    scope<
        E extends Record<string, any> = Record<string, any>,
        P extends string = string,
        M extends Record<string, any> = Record<string, any>,
        C = Context,
    >(): any {
        const [prefix, scopeObj, options] = parseScopeArgs(
            arguments,
            this.options.meta,
            this.options.context,
        );
        let scope: FastEventScope<ScopeEvents<Events, P> & E, FinalMeta & M, C>;
        if (scopeObj) {
            scope = scopeObj;
        } else {
            scope = new FastEventScope<ScopeEvents<Events, P> & E, FinalMeta & M, C>();
        }
        scope.bind(this.emitter as any, this.prefix + prefix, options);
        return scope;
    }
}
